import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Typography, Button, Select, MenuItem, FormControl, Link, Grid, CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CustomCheckbox from '../../../common/components/CustomCheckbox/CustomCheckbox';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { NoDataFound } from '../../../common/components/NoDataFound';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import { useNO3Report, type MailDataStructure, type OperatorData, type MailItem } from './hooks';
import Table from '../../../common/components/Table/Table';
import type { SortConfig, SortDirection } from '../../../common/components/Table/types';
import { PostcardsTable, PostcardSummaryTable, PostcardTotalSummaryTable } from './components';
import { 
  MainContent,
  ReportContainer,
  SectionContainer,
  SectionTitle,
  CheckboxGroup,
  ButtonContainer,
  ActionButton,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ResetButton,
  ExportButton
} from './NO3Report.styled';

// Restore the original interfaces needed for the component state
interface ServiceTypeState {
  aircraftPriority: boolean;
  salNonPriority: boolean;
  surfaceNonPriority: boolean;
  surfacePriority: boolean;
}

interface DespatchState {
  formatSeparated: boolean;
  pgeMixed: boolean;
  postcardsFormatC: boolean;
}

interface AccountingTypeState {
  gross: boolean;
  net: boolean;
}

interface DirectionOption {
  id: string;
  label: string;
}

// New interfaces for the report data structure
interface QuarterlyRow {
  quarter: string | null;
  gross: string;
  tare: string;
  net: string;
  zgross: string;
  ztare: string;
  spgross: string;
  sggross: string;
  segross: string;
  sjgross: string;
  szgross: string;
  ptare: string;
  gtare: string;
  etare: string;
  jgross: string;
  jtare: string;
  jitems: string;
  zitems: string;
  pitems: string;
  gitems: string;
  eitems: string;
  pnet: string;
  gnet: string;
  enet: string;
  pgross: string;
  ggross: string;
  egross: string;
}

interface ReportHeader {
  operator: string;
  year: string;
  serviceType: string;
  mailFormat: string;
  accountingType: string;
  rows: QuarterlyRow[];
  summary: SummaryRow[];
}

interface FileInfo {
  fileName: string;
  quarter: string;
}

interface SummaryRow {
  summary: string;
  formatP: string;
  formatG: string;
  formatE: string;
  allFormats: string;
}

interface PostcardLine {
  year: number | null;
  accountingType: string | null;
  mailCategory: string | null;
  quarter: number | null;
  sendingOe: string;
  receivingOe: string;
  gross: number;
  tare: number;
  net: number;
  netPge: number;
  numberOfItemsPge: number;
  receivedWeightStratum: number | null;
  receivedReceptaclesStratum: number | null;
  numberOfSampledReceptacles: number | null;
  effectiveSamplingRate: number | null;
}

interface PostcardSummary {
  summary: string;
  value: string;
}

interface TotalSummary {
  summary: string;
  gross: string;
  tare: string;
  net: string;
  netPge: string;
  numberOfItemsPge: string;
}

interface PostcardReportData {
  year: number;
  accountingType: string;
  mailCategory: string;
  postcardLines: PostcardLine[];
  totalSummary: TotalSummary;
  postcardSummary: PostcardSummary[];
}

// IPK Summary interfaces
interface IpkDetail {
  grossIpk: number | null;
  grossKg: number;
  items: number | null;
  grossIpkFormatted: string;
  grossKgFormatted: string;
  itemsFormatted: string;
}

interface IpkReportFormat {
  reportFormat: string | null;
  detailsPerMailCategory: {
    C: IpkDetail;
    B: IpkDetail;
    D: IpkDetail;
    A: IpkDetail;
  };
  reportTotal: IpkDetail;
  grossIpkTotal: number | null;
  grossKgTotal: number | null;
  itemsTotal: number | null;
}

interface IpkSummary {
  reportPerFormat: {
    P: IpkReportFormat;
    G: IpkReportFormat;
    E: IpkReportFormat;
    X: IpkReportFormat;
  };
  operator: string;
}

interface NewOperatorData {
  operator: string;
  files: FileInfo[];
  reportHeaders: ReportHeader[];
  postcardReport: PostcardReportData[];
  summary: SummaryRow[];
  grossIpkSummaries?: IpkSummary[];
}

type NewMailDataStructure = NewOperatorData[];

// Create columns for quarterly data table
const createQuarterlyColumns = () => [
  { 
    accessor: 'quarter', 
    Header: 'Quarter',
    Cell: ({ value }: { value: string | null }) => (
      <div style={{ 
        fontWeight: (value === null || value === undefined || value === '') ? 700 : 'normal', 
        textAlign: 'center', 
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'gross', 
    Header: 'Gross (kg)',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'tare', 
    Header: 'Tare (kg)',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'net', 
    Header: 'Net (kg)',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'pgross', 
    Header: 'P Gross (kg)',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'ptare', 
    Header: 'P Tare (kg)',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'pnet', 
    Header: 'P Net (kg)',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'pitems', 
    Header: 'P Items',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'ggross', 
    Header: 'G Gross (kg)',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'gtare', 
    Header: 'G Tare (kg)',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'gnet', 
    Header: 'G Net (kg)',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'gitems', 
    Header: 'G Items',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'egross', 
    Header: 'E Gross (kg)',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'etare', 
    Header: 'E Tare (kg)',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'enet', 
    Header: 'E Net (kg)',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'eitems', 
    Header: 'E Items',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  }
];

// Create columns for summary table
const createSummaryColumns = () => [
  { 
    accessor: 'summary', 
    Header: 'Summary',
    Cell: ({ value }: { value: string }) => (
      <div style={{ textAlign: 'left', width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'formatP', 
    Header: 'Format P',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'formatG', 
    Header: 'Format G',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'formatE', 
    Header: 'Format E',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'allFormats', 
    Header: 'All Formats',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  }
];

// Create columns for files table
const createFilesColumns = () => [
  { 
    accessor: 'fileName', 
    Header: 'File Name',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        fontFamily: 'monospace',
        fontSize: '12px',
        wordBreak: 'break-all',
        textAlign: 'left',
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}>
        {value}
      </div>
    )
  },
  { 
    accessor: 'quarter', 
    Header: 'Quarter',
    Cell: ({ value }: { value: string }) => (
      <div style={{ textAlign: 'center', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {value}
      </div>
    )
  }
];

// Create columns for IPK summary table
const createIpkColumns = () => [
  { 
    accessor: 'mailCategory', 
    Header: 'Mail Category',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        fontWeight: 600,
        textAlign: 'left', 
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        paddingLeft: '8px'
      }}>
        {value}
      </div>
    )
  },
  { 
    accessor: 'grossIpk', 
    Header: 'Gross IPK',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'grossKg', 
    Header: 'Gross Kg',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  },
  { 
    accessor: 'items', 
    Header: 'Items',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => (
      <div style={{ 
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'right',
        paddingRight: '8px'
      }}>
        {value || ''}
      </div>
    )
  }
];

// Transform IPK data for table display - returns separate tables for each format
const transformIpkData = (ipkSummary: IpkSummary) => {
  const { reportPerFormat } = ipkSummary;
  const formatTables: { [key: string]: any[] } = {};

  // Create separate tables for each format
  Object.entries(reportPerFormat).forEach(([formatKey, formatData]) => {
    // Include all formats, even if reportFormat is null (like X format)
    const tableData: any[] = [];
    
    // Add rows for each mail category in correct order (A, B, C, D)
    const categoryOrder = ['A', 'B', 'C', 'D'];
    categoryOrder.forEach((categoryKey) => {
      const categoryData = formatData.detailsPerMailCategory[categoryKey as keyof typeof formatData.detailsPerMailCategory];
      const row = {
        mailCategory: categoryKey,
        grossIpk: categoryData.grossIpkFormatted,
        grossKg: categoryData.grossKgFormatted,
        items: categoryData.itemsFormatted,
      };
      tableData.push(row);
    });
    
    // Add total row at the end
    const totalRow = {
      mailCategory: 'Total',
      grossIpk: formatData.reportTotal.grossIpkFormatted,
      grossKg: formatData.reportTotal.grossKgFormatted,
      items: formatData.reportTotal.itemsFormatted,
    };
    tableData.push(totalRow);
    
    // Use formatKey as the table identifier (P, G, E, X)
    formatTables[formatKey] = tableData;
  });

  return formatTables;
};

// Note: Postcard table now uses dedicated PostcardsTable component

// Generic client-side table with fixed total row at bottom (for IPK format tables)
const IpkFormatDataTable = React.memo<{ data: any[]; columns: any[]; direction: string }>(
  ({ data, columns, direction }) => {
    const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);

    function parseNumeric(value: unknown): number | null {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        const cleaned = value.replace(/[\'`,\s]/g, '');
        const num = Number(cleaned);
        if (Number.isFinite(num)) return num;
      }
      return null;
    }

    const sortedRows = useMemo(() => {
      if (!data || data.length === 0) return data;
      const totalRow = data[data.length - 1];
      const bodyRows = data.slice(0, data.length - 1);
      if (!sortConfig) return [...bodyRows, totalRow];

      const { column, direction: dir } = sortConfig;
      const multiplier = dir === 'ASC' ? 1 : -1;
      const sortedBody = [...bodyRows].sort((a: any, b: any) => {
        const av = a?.[column];
        const bv = b?.[column];
        const an = parseNumeric(av);
        const bn = parseNumeric(bv);
        if (av == null && bv == null) return 0;
        if (av == null) return -1 * multiplier;
        if (bv == null) return 1 * multiplier;
        if (an !== null && bn !== null) return (an - bn) * multiplier;
        const astr = String(av);
        const bstr = String(bv);
        return astr.localeCompare(bstr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
      });
      return [...sortedBody, totalRow];
    }, [data, sortConfig]);

    const handleSort = useCallback((column: string, direction: SortDirection | undefined) => {
      if (!direction) { setSortConfig(undefined); return; }
      setSortConfig({ column, direction });
    }, []);

    return (
      <Table
        data={sortedRows}
        columns={columns}
        viewMode={true}
        showPagination={false}
        disableAutoSizing={false}
        enableSearch={false}
        pageSize={10}
        rowBackgroundColor={direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
        rowClassName={(row: any) => row.mailCategory === 'Total' ? 'total-row' : ''}
        onSort={handleSort}
      />
    );
  }
);

// Memoized Results Section Component
const ResultsSection = React.memo<{
  htmlResults: NewMailDataStructure;
  quarterlyColumns: any[];
  summaryColumns: any[];
  filesColumns: any[];
  ipkColumns: any[];
  direction: string;
}>(({ htmlResults, quarterlyColumns, summaryColumns, filesColumns, ipkColumns, direction }) => {
  return (
    <Box sx={{ mt: 6 }}>
      
      {htmlResults.map((operatorData: NewOperatorData, operatorIndex: number) => (
        <NewOperatorSection 
          key={`${operatorData.operator}-${operatorIndex}`}
          operatorData={operatorData}
          operatorIndex={operatorIndex}
          quarterlyColumns={quarterlyColumns}
          summaryColumns={summaryColumns}
          filesColumns={filesColumns}
          ipkColumns={ipkColumns}
          direction={direction}
        />
      ))}
    </Box>
  );
});

// New Operator Section Component
const NewOperatorSection = React.memo<{
  operatorData: NewOperatorData;
  operatorIndex: number;
  quarterlyColumns: any[];
  summaryColumns: any[];
  filesColumns: any[];
  ipkColumns: any[];
  direction: string;
}>(({ operatorData, operatorIndex, quarterlyColumns, summaryColumns, filesColumns, ipkColumns, direction }) => {
  const [filesSortConfig, setFilesSortConfig] = useState<SortConfig | undefined>(undefined);
  const [summarySortConfig, setSummarySortConfig] = useState<SortConfig | undefined>(undefined);

  function parseNumeric(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[\'`,\s]/g, '');
      const num = Number(cleaned);
      if (Number.isFinite(num)) return num;
    }
    return null;
  }

  const sortedFiles = useMemo(() => {
    const rows = operatorData.files || [];
    if (!filesSortConfig) return rows;
    const { column, direction } = filesSortConfig;
    const multiplier = direction === 'ASC' ? 1 : -1;
    return [...rows].sort((a: any, b: any) => {
      const av = a?.[column];
      const bv = b?.[column];
      const an = parseNumeric(av);
      const bn = parseNumeric(bv);
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * multiplier;
      if (bv == null) return 1 * multiplier;
      if (an !== null && bn !== null) return (an - bn) * multiplier;
      const astr = String(av);
      const bstr = String(bv);
      return astr.localeCompare(bstr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
    });
  }, [operatorData.files, filesSortConfig]);

  const handleFilesSort = useCallback((column: string, direction: SortDirection | undefined) => {
    if (!direction) { setFilesSortConfig(undefined); return; }
    setFilesSortConfig({ column, direction });
  }, []);

  const sortedOperatorSummary = useMemo(() => {
    const rows = operatorData.summary || [];
    if (rows.length === 0) return rows;
    const totalRow = rows[rows.length - 1];
    const bodyRows = rows.slice(0, rows.length - 1);
    if (!summarySortConfig) return [...bodyRows, totalRow];
    const { column, direction } = summarySortConfig;
    const multiplier = direction === 'ASC' ? 1 : -1;
    const sortedBody = [...bodyRows].sort((a: any, b: any) => {
      const av = a?.[column];
      const bv = b?.[column];
      const an = parseNumeric(av);
      const bn = parseNumeric(bv);
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * multiplier;
      if (bv == null) return 1 * multiplier;
      if (an !== null && bn !== null) return (an - bn) * multiplier;
      const astr = String(av);
      const bstr = String(bv);
      return astr.localeCompare(bstr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
    });
    return [...sortedBody, totalRow];
  }, [operatorData.summary, summarySortConfig]);

  const handleSummarySort = useCallback((column: string, direction: SortDirection | undefined) => {
    if (!direction) { setSummarySortConfig(undefined); return; }
    setSummarySortConfig({ column, direction });
  }, []);
  return (
    <Box sx={{ 
      mb: 6,
      p: 3,
      border: '1px solid #E1E8ED',
      borderRadius: '8px',
      backgroundColor: '#FAFBFC'
    }}>
      {/* Operator Title */}
      <Box sx={{ 
        mb: 3,
        pb: 2,
        borderBottom: '1px solid #E1E8ED'
      }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 600, 
          color: '#063142', 
          fontSize: '20px',
          letterSpacing: '-0.25px'
        }}>
          Operator: {operatorData.operator}
        </Typography>
      </Box>

      {/* Files Section */}
      {operatorData.files && operatorData.files.length > 0 && (
        <Box sx={{ 
          mb: 4,
          p: 3,
          backgroundColor: '#FFFFFF',
          borderRadius: '6px',
          border: '1px solid #E1E8ED',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          maxWidth: '475px',
          alignItems: 'center',
          justifyContent: 'center',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: '#063142',
            fontSize: '16px',
            mb: 3
          }}>
            Files ({operatorData.files.length})
          </Typography>
          <Box sx={{ 
            '& .MuiPaper-root': {
              boxShadow: 'none',
              border: '1px solid #E1E8ED'
            }
          }}>
            <Table
              data={sortedFiles}
              columns={filesColumns}
              viewMode={true}
              disableAutoSizing={false}
              pageSize={10}
              showPagination={false}
              enableSearch={false}
              rowBackgroundColor={direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
              onSort={handleFilesSort}
            />
          </Box>
        </Box>
      )}
      
      {/* Report Headers */}
      {operatorData.reportHeaders.map((reportHeader: ReportHeader, headerIndex: number) => (
        <ReportHeaderSection
          key={`${reportHeader.serviceType}-${reportHeader.mailFormat}-${reportHeader.accountingType}-${headerIndex}`}
          reportHeader={reportHeader}
          headerIndex={headerIndex}
          quarterlyColumns={quarterlyColumns}
          summaryColumns={summaryColumns}
          direction={direction}
        />
      ))}

      {/* Summary Section */}
      {operatorData.summary && operatorData.summary.length > 0 && (
        <Box sx={{ 
          mt: 4,
          p: 3,
          backgroundColor: '#FFFFFF',
          borderRadius: '6px',
          border: '1px solid #E1E8ED',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: '#063142',
            fontSize: '16px',
            mb: 3
          }}>
            Summary Statistics
          </Typography>
          <Box sx={{ 
            '& .MuiPaper-root': {
              boxShadow: 'none',
              border: '1px solid #E1E8ED'
            }
          }}>
            <Table
              data={sortedOperatorSummary}
              columns={summaryColumns}
              moduleName={`${operatorData.operator}-summary`}
              viewMode={true}
              showPagination={false}
              disableAutoSizing={false}
              enableSearch={false}
              pageSize={10}
              rowBackgroundColor={direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
              rowClassName={(row: SummaryRow) => row.summary?.toLowerCase().includes('total') ? 'total-row' : ''}
              onSort={handleSummarySort}
            />
          </Box>
        </Box>
      )}

      {/* Postcard Report Section */}
      {operatorData.postcardReport && operatorData.postcardReport.length > 0 && (
        <Box sx={{ 
          mt: 4,
          p: 3,
          backgroundColor: '#FFFFFF',
          borderRadius: '6px',
          border: '1px solid #E1E8ED',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: '#063142',
            fontSize: '16px',
            mb: 3
          }}>
            Postcard Report Data
          </Typography>
          
          {operatorData.postcardReport.map((postcardReportData: PostcardReportData, reportIndex: number) => (
            <Box key={`${postcardReportData.mailCategory}-${postcardReportData.accountingType}-${reportIndex}`} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                color: '#063142',
                fontSize: '14px',
                mb: 2
              }}>
                Mail Category {postcardReportData.mailCategory} - {postcardReportData.accountingType} ({postcardReportData.year})
              </Typography>
              
              {/* Main Postcard Lines Table */}
              <Box sx={{ mb: 3 }}>
                <PostcardsTable 
                  data={postcardReportData.postcardLines}
                  direction={direction}
                />
              </Box>

              {/* Total Summary Table */}
              {postcardReportData.totalSummary && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    color: '#063142',
                    fontSize: '13px',
                    mb: 1
                  }}>
                    Total Summary
                  </Typography>
                  <PostcardTotalSummaryTable 
                    data={postcardReportData.totalSummary}
                    direction={direction}
                  />
                </Box>
              )}

              {/* Postcard Summary Table */}
              {postcardReportData.postcardSummary && postcardReportData.postcardSummary.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    color: '#063142',
                    fontSize: '13px',
                    mb: 1
                  }}>
                    Summary Statistics
                  </Typography>
                  <PostcardSummaryTable 
                    data={postcardReportData.postcardSummary}
                    direction={direction}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* IPK Summary Section */}
      {operatorData.grossIpkSummaries && operatorData.grossIpkSummaries.length > 0 && (
        <Box sx={{ 
          mt: 4,
          p: 3,
          backgroundColor: '#FFFFFF',
          borderRadius: '6px',
          border: '1px solid #E1E8ED',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: '#063142',
            fontSize: '16px',
            mb: 3
          }}>
            IPK Summary
          </Typography>
          
          {operatorData.grossIpkSummaries.map((ipkSummary: IpkSummary, ipkIndex: number) => {
            const formatTables = transformIpkData(ipkSummary);
            
            return (
              <Box key={`ipk-${ipkSummary.operator}-${ipkIndex}`} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ 
                  fontWeight: 600, 
                  color: '#063142',
                  fontSize: '14px',
                  mb: 2
                }}>
                  Gross IPK Summary - {ipkSummary.operator}
                </Typography>
                
                {/* Display separate tables for each format */}
                {Object.entries(formatTables).map(([formatKey, tableData]) => (
                  <Box key={`format-${formatKey}`} sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 600, 
                      color: '#063142',
                      fontSize: '13px',
                      mb: 1
                    }}>
                      Format {formatKey}
                    </Typography>
                    
                    <Box sx={{ 
                      '& .MuiPaper-root': {
                        boxShadow: 'none',
                        border: '1px solid #E1E8ED'
                      }
                    }}>
            <IpkFormatDataTable data={tableData as any[]} columns={ipkColumns} direction={direction} />
                    </Box>
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
});

// Report Header Section Component
const ReportHeaderSection = React.memo<{
  reportHeader: ReportHeader;
  headerIndex: number;
  quarterlyColumns: any[];
  summaryColumns: any[];
  direction: string;
}>(({ reportHeader, headerIndex, quarterlyColumns, summaryColumns, direction }) => {
  const [quarterlySortConfig, setQuarterlySortConfig] = useState<SortConfig | undefined>(undefined);
  const [headerSummarySortConfig, setHeaderSummarySortConfig] = useState<SortConfig | undefined>(undefined);

  function parseNumeric(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[\'`,\s]/g, '');
      const num = Number(cleaned);
      if (Number.isFinite(num)) return num;
    }
    return null;
  }

  const sortedQuarterRows = useMemo(() => {
    const rows = reportHeader.rows || [];
    if (rows.length === 0) return rows;
    const isTotal = (r: any) => r.quarter === null || r.quarter === undefined || r.quarter === '';
    const totalRow = rows[rows.length - 1];
    const bodyRows = isTotal(totalRow) ? rows.slice(0, rows.length - 1) : rows.slice();
    if (!quarterlySortConfig) return isTotal(totalRow) ? [...bodyRows, totalRow] : bodyRows;
    const { column, direction } = quarterlySortConfig;
    const multiplier = direction === 'ASC' ? 1 : -1;
    const sortedBody = [...bodyRows].sort((a: any, b: any) => {
      const av = a?.[column];
      const bv = b?.[column];
      const an = parseNumeric(av);
      const bn = parseNumeric(bv);
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * multiplier;
      if (bv == null) return 1 * multiplier;
      if (an !== null && bn !== null) return (an - bn) * multiplier;
      const astr = String(av);
      const bstr = String(bv);
      return astr.localeCompare(bstr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
    });
    return isTotal(totalRow) ? [...sortedBody, totalRow] : sortedBody;
  }, [reportHeader.rows, quarterlySortConfig]);

  const handleQuarterlySort = useCallback((column: string, direction: SortDirection | undefined) => {
    if (!direction) { setQuarterlySortConfig(undefined); return; }
    setQuarterlySortConfig({ column, direction });
  }, []);

  const sortedHeaderSummary = useMemo(() => {
    const rows = reportHeader.summary || [];
    if (rows.length === 0) return rows;
    const isTotal = (r: any) => typeof r.summary === 'string' && r.summary.toLowerCase().includes('total');
    const totalRow = rows[rows.length - 1];
    const bodyRows = isTotal(totalRow) ? rows.slice(0, rows.length - 1) : rows.slice();
    if (!headerSummarySortConfig) return isTotal(totalRow) ? [...bodyRows, totalRow] : bodyRows;
    const { column, direction } = headerSummarySortConfig;
    const multiplier = direction === 'ASC' ? 1 : -1;
    const sortedBody = [...bodyRows].sort((a: any, b: any) => {
      const av = a?.[column];
      const bv = b?.[column];
      const an = parseNumeric(av);
      const bn = parseNumeric(bv);
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * multiplier;
      if (bv == null) return 1 * multiplier;
      if (an !== null && bn !== null) return (an - bn) * multiplier;
      const astr = String(av);
      const bstr = String(bv);
      return astr.localeCompare(bstr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
    });
    return isTotal(totalRow) ? [...sortedBody, totalRow] : sortedBody;
  }, [reportHeader.summary, headerSummarySortConfig]);

  const handleHeaderSummarySort = useCallback((column: string, direction: SortDirection | undefined) => {
    if (!direction) { setHeaderSummarySortConfig(undefined); return; }
    setHeaderSummarySortConfig({ column, direction });
  }, []);
  return (
    <Box sx={{ 
      mb: 4,
      p: 3,
      backgroundColor: '#FFFFFF',
      borderRadius: '6px',
      border: '1px solid #E1E8ED',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    }}>
      {/* Table Header Info */}
      <Box sx={{ 
        mb: 3,
        p: 2,
        backgroundColor: '#F8F9FA',
        borderRadius: '4px',
        border: '1px solid #E9ECEF'
      }}>
        <Typography variant="h6" sx={{ 
          fontWeight: 600, 
          color: '#063142',
          fontSize: '16px',
          mb: 1
        }}>
          Year: {reportHeader.year} | Service Type: {reportHeader.serviceType} | Mail Format: {reportHeader.mailFormat} | Accounting Type: {reportHeader.accountingType}
        </Typography>
      </Box>
      
      {/* Quarterly Data Section */}
      <Box sx={{ mb: 3 }}>
        {reportHeader.rows.length === 0 ? (
          <Box sx={{ 
            p: 3,
            textAlign: 'center',
            backgroundColor: '#FFF8E1',
            borderRadius: '4px',
            border: '1px dashed #FFE082'
          }}>
            <Typography sx={{ 
              fontStyle: 'italic', 
              color: '#F57F17',
              fontSize: '14px',
              fontWeight: 500
            }}>
              ⚠️ No quarterly data available
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            '& .MuiPaper-root': {
              boxShadow: 'none',
              border: '1px solid #E1E8ED'
            }
          }}>
            <Table
              data={sortedQuarterRows}
              columns={quarterlyColumns}
              viewMode={true}
              disableAutoSizing={false}
              showPagination={false}
              pageSize={25}
              enableSearch={false}
              rowBackgroundColor={direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
              rowClassName={(row: QuarterlyRow) => (row.quarter === null || row.quarter === undefined || row.quarter === '') ? 'total-row' : ''}
              onSort={handleQuarterlySort}
            />
          </Box>
        )}
      </Box>

      {/* Summary Section for this Report Header */}
      {reportHeader.summary && reportHeader.summary.length > 0 && (
        <Box sx={{ maxWidth: '600px', alignItems: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ 
            '& .MuiPaper-root': {
              boxShadow: 'none',
              border: '1px solid #E1E8ED'
            }
          }}>
            <Table
              data={sortedHeaderSummary}
              columns={summaryColumns}
              viewMode={true}
              disableAutoSizing={false}
              showPagination={false}
              pageSize={10}
              enableSearch={false}
              rowBackgroundColor={direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
              rowClassName={(row: SummaryRow) => row.summary?.toLowerCase().includes('total') ? 'total-row' : ''}
              onSort={handleHeaderSummarySort}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
});

function NO3Report() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [ppoValue, setPpoValue] = useState(() => {
    const ppoParam = searchParams.get('ppo');
    return ppoParam ? (ppoParam === 'All' ? 'All' : ppoParam) : 'All';
  });
  const [ppoFilter, setPpoFilter] = useState(() => {
    const typeParam = searchParams.get('type');
    return typeParam === 'outbound' ? 'Outbound' : 'Inbound';
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    const yearParam = searchParams.get('year');
    return yearParam || '';
  });
  const [includeIpkSummary, setIncludeIpkSummary] = useState<boolean>(false);

  // Add state for HTML results
  const [htmlResults, setHtmlResults] = useState<NewMailDataStructure | null>(null);
  // Add state to track when no data is found
  const [noDataFound, setNoDataFound] = useState<boolean>(false);
  // Add state to track the direction used for current results
  const [resultsDirection, setResultsDirection] = useState<string>('');

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'error'
  });

  // Get effective operators and cascading logic for eno1 module
  const {
    operators,
    availableYears,
    operatorsForYear,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('eno1', { selectedYear, selectedPpo: ppoValue === 'All' ? undefined : ppoValue });

  // NO3 Report hook
  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
    error: reportError,
    reset: resetReportError
  } = useNO3Report();

  // Map the target DOs to PPO options with cascading logic
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];
    const targetDOsToUse = selectedYear ? operatorsForYear : operators;
    targetDOsToUse.forEach((targetDO) => {
      options.push({ id: targetDO, label: targetDO, hasLicense: true });
    });
    return options;
  }, [operators, operatorsForYear, selectedYear]);

  // Generate year options with cascading logic
  const yearOptions = React.useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const yearsToShow = ppoValue !== 'All' ? yearsForOperator : availableYears;
    yearsToShow.forEach((year) => {
      options.push({ value: year.toString(), label: year.toString() });
    });
    return options;
  }, [availableYears, yearsForOperator, ppoValue]);

  // Service Type state
  const [serviceType, setServiceType] = useState<ServiceTypeState>({
    aircraftPriority: true,
    salNonPriority: true,
    surfaceNonPriority: true,
    surfacePriority: true,
  });

  // Despatch state
  const [despatch, setDespatch] = useState<DespatchState>({
    formatSeparated: true,
    pgeMixed: true,
    postcardsFormatC: false,
  });

  // Accounting Type state
  const [accountingType, setAccountingType] = useState<AccountingTypeState>({
    gross: true,
    net: true,
  });

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/eno1');
  };

  const handleDirectionChange = (id: string) => {
    setPpoFilter(id);
  };

  // Handle DO selection change — years are independent of the selected DO
  const handleDOChange = (value: string) => {
    setPpoValue(value);
  };

  // Handle year selection change with cascading behavior
  const handleYearChange = (value: string) => {
    // Only clear DO selection if the currently selected DO is not available for the new year
    if (selectedYear !== value && ppoValue !== 'All') {
      // Check if the current DO is available for the new year
      const yearKey = value;
      const relationsForNewYear = allRelationsData?.byYear?.[yearKey];
      const isCurrentDOAvailableForNewYear = relationsForNewYear?.some(relation => 
        relation.targetDO === ppoValue &&
        relation.modules.some(module => 
          module.name === 'eno1' && module.enabled === true
        )
      );
      
      if (!isCurrentDOAvailableForNewYear) {
        setPpoValue('All');
      }
    }
    setSelectedYear(value);
  };

  const handleServiceTypeChange = (field: keyof ServiceTypeState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setServiceType(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleDespatchChange = (field: keyof DespatchState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setDespatch(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleAccountingTypeChange = (field: keyof AccountingTypeState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setAccountingType(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleSelectAllServiceType = () => {
    const allSelected = serviceType.aircraftPriority && 
                       serviceType.salNonPriority && 
                       serviceType.surfaceNonPriority && 
                       serviceType.surfacePriority;
    
    if (allSelected) {
      // Deselect all
      setServiceType({
        aircraftPriority: false,
        salNonPriority: false,
        surfaceNonPriority: false,
        surfacePriority: false,
      });
    } else {
      // Select all
      setServiceType({
        aircraftPriority: true,
        salNonPriority: true,
        surfaceNonPriority: true,
        surfacePriority: true,
      });
    }
  };

  const handleSelectAllDespatch = () => {
    const allSelected = despatch.formatSeparated && 
                       despatch.pgeMixed && 
                       despatch.postcardsFormatC;
    
    if (allSelected) {
      // Deselect all
      setDespatch({
        formatSeparated: false,
        pgeMixed: false,
        postcardsFormatC: false,
      });
    } else {
      // Select all
      setDespatch({
        formatSeparated: true,
        pgeMixed: true,
        postcardsFormatC: true,
      });
    }
  };

  const handleSelectAllAccountingType = () => {
    const allSelected = accountingType.gross && accountingType.net;
    
    if (allSelected) {
      // Deselect all
      setAccountingType({
        gross: false,
        net: false,
      });
    } else {
      // Select all
      setAccountingType({
        gross: true,
        net: true,
      });
    }
  };

  const handleReset = () => {
    setPpoValue('All');
    setPpoFilter('Inbound');
    setSelectedYear('');
    setIncludeIpkSummary(false);
    setServiceType({
      aircraftPriority: true,
      salNonPriority: true,
      surfaceNonPriority: true,
      surfacePriority: true,
    });
    setDespatch({
      formatSeparated: true,
      pgeMixed: true,
      postcardsFormatC: false,
    });
    setAccountingType({
      gross: true,
      net: true,
    });
    // Clear no data found state and results when resetting
    setNoDataFound(false);
    setHtmlResults(null);
    setResultsDirection('');
  };

  // Map checkbox states to API parameter values
  const getServiceTypeValues = (): string[] => {
    const values: string[] = [];
    if (serviceType.aircraftPriority) values.push('A');
    if (serviceType.salNonPriority) values.push('B');
    if (serviceType.surfaceNonPriority) values.push('C');
    if (serviceType.surfacePriority) values.push('D');
    return values;
  };

  const getMailFormatValues = (): string[] => {
    const values: string[] = [];
    if (despatch.formatSeparated) values.push('S');
    if (despatch.pgeMixed) values.push('X');
    if (despatch.postcardsFormatC) values.push('C');
    return values;
  };

  const getAccountingTypeValues = (): string[] => {
    const values: string[] = [];
    if (accountingType.gross) values.push('G');
    if (accountingType.net) values.push('N');
    return values;
  };

  // Function to apply current filters to SectionBar (URL params)
  const applyFiltersToSectionBar = () => {
    const newParams = new URLSearchParams();

    // Map PPO filter
    if (ppoValue) {
      newParams.append('ppo', ppoValue);
    } else {
      newParams.append('ppo', 'All');
    }

    // Map direction filter to type
    newParams.append('type', ppoFilter.toLowerCase());

    // Map selected year
    if (selectedYear) {
      newParams.append('year', selectedYear);
    } else {
      newParams.append('year', 'all');
    }

    // Add custom NO3Report-specific filters
    const serviceTypes = getServiceTypeValues();
    const mailFormats = getMailFormatValues();
    const accountingTypes = getAccountingTypeValues();

    if (serviceTypes.length > 0) {
      newParams.append('serviceTypes', serviceTypes.join(','));
    }

    if (mailFormats.length > 0) {
      newParams.append('mailFormats', mailFormats.join(','));
    }

    if (accountingTypes.length > 0) {
      newParams.append('accountingTypes', accountingTypes.join(','));
    }

    if (includeIpkSummary) {
      newParams.append('includeIpkSummary', 'true');
    }

    setSearchParams(newParams);
  };

  const handleExport = (format: 'HTML' | 'PDF' | 'XLS') => {
    // Reset any previous errors
    resetReportError();

    // Validate required fields
    const serviceTypes = getServiceTypeValues();
    const mailFormats = getMailFormatValues();
    const accountingTypes = getAccountingTypeValues();

    if (serviceTypes.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one Service Type',
        severity: 'error'
      });
      return;
    }

    if (mailFormats.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one Despatch option',
        severity: 'error'
      });
      return;
    }

    if (accountingTypes.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one Accounting Type',
        severity: 'error'
      });
      return;
    }

    if (!selectedYear) {
      setSnackbar({
        open: true,
        message: 'Please select a Year',
        severity: 'error'
      });
      return;
    }

    // Validate IPK summary year requirement
    if (includeIpkSummary && parseInt(selectedYear) < 2017) {
      setSnackbar({
        open: true,
        message: 'IPK summary is only available for year 2017 or more recent',
        severity: 'error'
      });
      return;
    }

    // Prepare API parameters
    const params = {
      serviceTypes,
      mailFormats,
      accountingTypes,
      formatType: format.toLowerCase() as 'html' | 'pdf' | 'xls',
      accountingYear: parseInt(selectedYear),
      inbound: ppoFilter === 'Inbound',
      ipk: includeIpkSummary,
      ...(ppoValue !== 'All' && { designatedOperator: ppoValue }),
    };

    generateReport(params, {
      onSuccess: (response) => {
        if (format === 'HTML') {
          // For HTML, display the tables data received from the API
          console.log('HTML Report tables generated:', response.data);
          console.log('Current filter params used:', params);
          
          // Check for 204 No Content or empty data
          if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
            // Set no data found state instead of clearing results
            setNoDataFound(true);
            setHtmlResults(null);
            // Don't apply filters to SectionBar when no data is found
            return;
          }

          if (response.data) {
            // Store the data in a variable to ensure type safety
            const newData: NewMailDataStructure = response.data as unknown as NewMailDataStructure;
            // Clear no data found state and set new data
            setNoDataFound(false);
            setHtmlResults(null);
            // Store the direction used for this report
            setResultsDirection(ppoFilter);
            // Use setTimeout to ensure the state is cleared before setting new data
            setTimeout(() => {
              setHtmlResults(newData);
              console.log('Updated htmlResults state with new data:', newData);
            }, 0);
            // Apply current filters to SectionBar only when data is found
            applyFiltersToSectionBar();
          }
        } else {
          // For PDF/XLS, download is handled automatically in the hook
          console.log(`${format} Report downloaded successfully with filename: ${response.filename}`);
        }
      },
      onError: async (error) => {
        console.error('Error generating report:', error);
        
        // Handle NOT_FOUND errors for PDF/XLS exports
        if (format !== 'HTML') {
          let errorMessage = 'An error occurred while generating the report';
          let errorStatus: string | undefined;
          
          // Check if it's an axios error
          if (axios.isAxiosError(error)) {
            // For blob responses, the error data might be a Blob containing JSON
            if (error.response?.data instanceof Blob) {
              try {
                const text = await error.response.data.text();
                const errorData = JSON.parse(text);
                errorMessage = errorData.message || errorMessage;
                errorStatus = errorData.status;
              } catch (parseError) {
                // If parsing fails, use default message
                console.error('Failed to parse error response:', parseError);
              }
            } else if (error.response?.data && typeof error.response.data === 'object') {
              // For non-blob responses, check the data directly
              const errorData = error.response.data as { message?: string; status?: string };
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            }
            
            // Check status code as fallback
            if (!errorStatus && error.response?.status === 404) {
              errorStatus = 'NOT_FOUND';
            }
          }
          
          // Show snackbar with appropriate message
          if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No ENO3 reports available')) {
            setSnackbar({
              open: true,
              message: errorMessage || 'No ENO3 reports available for the selected criteria',
              severity: 'warning'
            });
          } else {
            setSnackbar({
              open: true,
              message: errorMessage,
              severity: 'error'
            });
          }
        } else {
          // For HTML format, errors are already handled in the success callback
          setSnackbar({
            open: true,
            message: error instanceof Error ? error.message : 'An error occurred while generating the report',
            severity: 'error'
          });
        }
      }
    });
  };

  // Initial URL parameter sync when page loads
  useEffect(() => {
    const ppoParam = searchParams.get('ppo');
    const typeParam = searchParams.get('type');
    const yearParam = searchParams.get('year');

    // Sync PPO parameter
    if (ppoParam) {
      const newPpoValue = ppoParam === 'All' ? 'All' : ppoParam;
      setPpoValue(newPpoValue);
    }

    // Sync type parameter (direction filter)
    if (typeParam) {
      const mappedFilter = typeParam === 'outbound' ? 'Outbound' : 'Inbound';
      setPpoFilter(mappedFilter);
    }

    // Sync year parameter
    if (yearParam) {
      setSelectedYear(yearParam);
    }
  }, [searchParams]);

  // Note: Removed the URL sync effect that was overriding SectionBar filter changes
  // The SectionBar filters should work independently without being overridden by URL params

  // Memoize clearResults callback
  const clearResults = useCallback(() => {
    setHtmlResults(null);
    setNoDataFound(false);
    setResultsDirection('');
    // Clear URL parameters when results are cleared
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Handle snackbar close
  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Helper functions to determine button text
  const getServiceTypeButtonText = () => {
    const allSelected = serviceType.aircraftPriority && 
                       serviceType.salNonPriority && 
                       serviceType.surfaceNonPriority && 
                       serviceType.surfacePriority;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  const getDespatchButtonText = () => {
    const allSelected = despatch.formatSeparated && 
                       despatch.pgeMixed && 
                       despatch.postcardsFormatC;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  const getAccountingTypeButtonText = () => {
    const allSelected = accountingType.gross && accountingType.net;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  // Handler functions for SectionBar custom filter changes
  const handleSectionBarServiceTypesChange = useCallback((values: string[]) => {
    setServiceType({
      aircraftPriority: values.includes('A'),
      salNonPriority: values.includes('B'),
      surfaceNonPriority: values.includes('C'),
      surfacePriority: values.includes('D'),
    });
  }, []);

  const handleSectionBarMailFormatsChange = useCallback((values: string[]) => {
    setDespatch({
      formatSeparated: values.includes('S'),
      pgeMixed: values.includes('X'),
      postcardsFormatC: values.includes('C'),
    });
  }, []);

  const handleSectionBarAccountingTypesChange = useCallback((values: string[]) => {
    setAccountingType({
      gross: values.includes('G'),
      net: values.includes('N'),
    });
  }, []);

  const handleSectionBarIpkSummaryChange = useCallback((checked: boolean) => {
    setIncludeIpkSummary(checked);
  }, []);

  const handleSectionBarYearChange = useCallback((year: string) => {
    setSelectedYear(year);
  }, []);

  // Handler for SectionBar direction change
  const handleSectionBarDirectionChange = useCallback((direction: string) => {
    setPpoFilter(direction);
  }, []);

  // Handler for SectionBar PPO change
  const handleSectionBarPpoChange = useCallback((ppo: string) => {
    handleDOChange(ppo === 'All' ? 'All' : ppo);
  }, []);

  // Create custom filters object for SectionBar
  const customFiltersForSectionBar = useMemo(() => {
    // Only show SectionBar custom filters when we have actual results (not when no data found)
    if (!htmlResults || noDataFound) return undefined;

    return {
      serviceTypes: getServiceTypeValues(),
      mailFormats: getMailFormatValues(),
      accountingTypes: getAccountingTypeValues(),
      includeIpkSummary: includeIpkSummary,
      direction: ppoFilter,
      year: selectedYear,
      // Pass cascaded PPO options and current PPO value for SectionBar
      ppo: ppoValue,
      ppoOptions: ppoOptions.map(option => ({
        id: option.id,
        name: option.label,
        disabled: false
      })),
      isPpoLoading: isPpoLoading,
      // Pass cascaded year options for SectionBar
      yearOptions: yearOptions.map(option => option.value),
      onServiceTypesChange: handleSectionBarServiceTypesChange,
      onMailFormatsChange: handleSectionBarMailFormatsChange,
      onAccountingTypesChange: handleSectionBarAccountingTypesChange,
      onIpkSummaryChange: handleSectionBarIpkSummaryChange,
      onDirectionChange: handleSectionBarDirectionChange,
      onYearChange: handleSectionBarYearChange,
      onPpoChange: handleSectionBarPpoChange,
      onExport: handleExport,
      isExporting: isGeneratingReport,
    };
  }, [
    htmlResults,
    noDataFound,
    serviceType,
    despatch,
    accountingType,
    includeIpkSummary,
    ppoFilter,
    selectedYear,
    ppoValue,
    ppoOptions,
    isPpoLoading,
    yearOptions,
    handleSectionBarServiceTypesChange,
    handleSectionBarMailFormatsChange,
    handleSectionBarAccountingTypesChange,
    handleSectionBarIpkSummaryChange,
    handleSectionBarDirectionChange,
    handleSectionBarYearChange,
    handleSectionBarPpoChange,
    handleExport,
    isGeneratingReport,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='no3-report-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='NO3 Report'
            subtitle='eNO1 Sampling'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            showFilters={{
              ppo: !!htmlResults && !noDataFound,
              type: !!htmlResults && !noDataFound,
              year: !!htmlResults && !noDataFound,
              customNO3: !!htmlResults && !noDataFound,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid="section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="no3-report-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='no3-report-main-content'>
            <ReportContainer data-testid="no3-report-content">
              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3 
              }} data-testid="no3-report-header">
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#063142' }} data-testid="no3-report-title">
                  NO3 Report
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }} data-testid="no3-report-header-actions">
                  {(htmlResults || noDataFound) && (
                    <ClearResultsButton 
                      variant="outlined" 
                      onClick={clearResults}
                      data-testid="no3-report-clear-results-btn"
                    >
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Main Filter Section - Hide when HTML results exist (but show when no data found) */}
              {!htmlResults && (
                <>
                  <FilterSection>
                    {/* PPO, Year, and Direction Filter in one row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          DO
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={ppoValue}
                              onChange={(e) => handleDOChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 160 }}
                              disabled={isPpoLoading}
                            >
                              <MenuItem value="All">All</MenuItem>
                              {isPpoLoading && (
                                <MenuItem value="" disabled>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <span>Loading...</span>
                                  </Box>
                                </MenuItem>
                              )}
                              {!isPpoLoading && !ppoError && ppoOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <span>{option.label}</span>
                                  </Box>
                                </MenuItem>
                              ))}
                              {ppoError && (
                                <MenuItem disabled>Error loading PPOs</MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          Year *
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth required>
                            <Select
                              value={selectedYear}
                              onChange={(e) => handleYearChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 120 }}
                            >
                              <MenuItem value="">Select</MenuItem>
                              {yearOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                      
                      {/* Direction Filter using Segmented Control */}
                      <StyledSegmentedControl>
                        {directionOptions.map((option) => (
                          <StyledSegmentButton
                            key={option.id}
                            active={ppoFilter === option.id}
                            onClick={() => handleDirectionChange(option.id)}
                            sx={{ 
                              color: ppoFilter === option.id ? '#ffffff' : '#00B6BA',
                              minWidth: '70px'
                            }}
                          >
                            {option.label}
                          </StyledSegmentButton>
                        ))}
                      </StyledSegmentedControl>
                    </Box>

                    {/* IPK Summary Checkbox */}
                    <Box sx={{ mb: 2 }}>
                      <CustomCheckbox
                        checked={includeIpkSummary}
                        onChange={(event) => setIncludeIpkSummary(event.target.checked)}
                        label="Include yearly IPK summary for UPU CN61 (HTML or PDF only)"
                      />
                      <Typography sx={{ 
                        fontSize: '12px', 
                        color: '#666', 
                        mt: 0.5, 
                        ml: 3.5,
                        fontStyle: 'italic'
                      }}>
                        Note that all service types, both inward and format despatched and only data with accounting type = gross are taken into account for this report.
                      </Typography>
                      {/* IPK Year Validation Error */}
                      {includeIpkSummary && selectedYear && parseInt(selectedYear) < 2017 && (
                        <Typography sx={{ 
                          fontSize: '12px', 
                          color: '#d32f2f', 
                          mt: 0.5, 
                          ml: 3.5,
                          fontWeight: 500
                        }}>
                          IPK summary is only available for year 2017 or more recent
                        </Typography>
                      )}
                    </Box>
                  </FilterSection>

                  <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      mb: 2 
                    }} />

                  {/* Main Content Grid - without borders, with dividers */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    width: '100%'
                  }}>
                    {/* Service Type */}
                    <Box sx={{ 
                      flex: { xs: 'none', md: '1 1 0' },
                      padding: 2,
                      minWidth: 0
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <SectionTitle>Service Type</SectionTitle>
                        <Link 
                          component="button"
                          onClick={handleSelectAllServiceType}
                          sx={{ 
                            color: '#00ACE7', 
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none'
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC'
                            }
                          }}
                        >
                          {getServiceTypeButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={serviceType.aircraftPriority}
                          onChange={handleServiceTypeChange('aircraftPriority')}
                          label="A - Airmail Priority"
                        />
                        <CustomCheckbox
                          checked={serviceType.salNonPriority}
                          onChange={handleServiceTypeChange('salNonPriority')}
                          label="B - SAL Non-Priority"
                        />
                        <CustomCheckbox
                          checked={serviceType.surfaceNonPriority}
                          onChange={handleServiceTypeChange('surfaceNonPriority')}
                          label="C - Surface Non-Priority"
                        />
                        <CustomCheckbox
                          checked={serviceType.surfacePriority}
                          onChange={handleServiceTypeChange('surfacePriority')}
                          label="D - Surface Priority"
                        />
                      </CheckboxGroup>
                    </Box>

                    {/* Vertical Divider */}
                    <Box sx={{ 
                      width: '1px', 
                      backgroundColor: '#E1E8ED',
                      display: { xs: 'none', md: 'block' }
                    }} />

                    {/* Horizontal Divider for mobile */}
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      my: 2,
                      display: { xs: 'block', md: 'none' }
                    }} />

                    {/* Despatch */}
                    <Box sx={{ 
                      flex: { xs: 'none', md: '1 1 0' },
                      padding: 2,
                      minWidth: 0
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <SectionTitle>Despatch</SectionTitle>
                        <Link 
                          component="button"
                          onClick={handleSelectAllDespatch}
                          sx={{ 
                            color: '#00ACE7', 
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none'
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC'
                            }
                          }}
                        >
                          {getDespatchButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={despatch.formatSeparated}
                          onChange={handleDespatchChange('formatSeparated')}
                          label="Format Separated"
                        />
                        <CustomCheckbox
                          checked={despatch.pgeMixed}
                          onChange={handleDespatchChange('pgeMixed')}
                          label="PGE Mixed"
                        />
                        <CustomCheckbox
                          checked={despatch.postcardsFormatC}
                          onChange={handleDespatchChange('postcardsFormatC')}
                          label="Postcards Format C"
                        />
                      </CheckboxGroup>
                    </Box>

                    {/* Vertical Divider */}
                    <Box sx={{ 
                      width: '1px', 
                      backgroundColor: '#E1E8ED',
                      display: { xs: 'none', md: 'block' }
                    }} />

                    {/* Horizontal Divider for mobile */}
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      my: 2,
                      display: { xs: 'block', md: 'none' }
                    }} />

                    {/* Accounting Type */}
                    <Box sx={{ 
                      flex: { xs: 'none', md: '1 1 0' },
                      padding: 2,
                      minWidth: 0
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <SectionTitle>Accounting Type</SectionTitle>
                        <Link 
                          component="button"
                          onClick={handleSelectAllAccountingType}
                          sx={{ 
                            color: '#00ACE7', 
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none'
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC'
                            }
                          }}
                        >
                          {getAccountingTypeButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={accountingType.gross}
                          onChange={handleAccountingTypeChange('gross')}
                          label="Gross"
                        />
                        <CustomCheckbox
                          checked={accountingType.net}
                          onChange={handleAccountingTypeChange('net')}
                          label="Net"
                        />
                      </CheckboxGroup>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <ButtonContainer sx={{ justifyContent: 'flex-end' }}>
                    <ResetButton 
                      variant="contained" 
                      onClick={handleReset}
                    >
                      RESET
                    </ResetButton>
                    <ExportButton 
                      variant="contained" 
                      onClick={() => handleExport('HTML')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'HTML'}
                    </ExportButton>
                    <ExportButton 
                      variant="contained" 
                      onClick={() => handleExport('PDF')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'PDF'}
                    </ExportButton>
                    <ExportButton 
                      variant="contained" 
                      onClick={() => handleExport('XLS')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'XLS'}
                    </ExportButton>
                  </ButtonContainer>
                </>
              )}

              {/* No Data Found Section */}
              {noDataFound && (
                <NoDataFound
                  title="No Data Available"
                  message="No NO3 report data was found for the selected criteria. Please try adjusting your filters or selecting a different date range."
                  onClearResults={clearResults}
                  onResetFilters={handleReset}
                />
              )}

              {/* Results Section - Show tables directly under filters */}
              {htmlResults && !noDataFound && (
                <ResultsSection htmlResults={htmlResults} quarterlyColumns={createQuarterlyColumns()} summaryColumns={createSummaryColumns()} filesColumns={createFilesColumns()} ipkColumns={createIpkColumns()} direction={resultsDirection} />
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid="no3-notification"
      />
    </Box>
  );
}

export default NO3Report; 